import { QueryClient } from '@tanstack/react-query';
import { isApiError } from './api-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Client errors (validation, permissions) will not fix themselves: no retry.
      retry: (failureCount, error) =>
        isApiError(error) && error.status > 0 && error.status < 500 ? false : failureCount < 2,
    },
  },
});

/** Every query key in one place, so invalidations never miss. */
export const queryKeys = {
  me: ['auth', 'me'] as const,
  publicContext: ['app', 'public'] as const,
  appContext: ['app', 'context'] as const,
  myLocations: ['locations', 'mine'] as const,
  company: ['company'] as const,
  companyHistory: ['company', 'history'] as const,
  settings: ['configuration', 'settings'] as const,
  sequences: ['configuration', 'sequences'] as const,
  referenceList: (list: string) => ['configuration', 'lists', list] as const,
  homeSections: ['configuration', 'home-sections'] as const,
  /** SEO-01: Search & social. */
  seoSettings: ['configuration', 'seo'] as const,
  featuredProducts: (query: Record<string, unknown>) =>
    ['configuration', 'home-sections', 'products', query] as const,
  channels: ['configuration', 'channels'] as const,
  users: ['users'] as const,
  roles: ['roles'] as const,
  transactions: (filters: Record<string, unknown> = {}) => ['transactions', filters] as const,
  products: ['products'] as const,
  productList: (query: Record<string, unknown>) => ['products', 'list', query] as const,
  product: (id: string) => ['products', 'detail', id] as const,
  productHistory: (id: string) => ['products', 'detail', id, 'history'] as const,
  productCatalog: ['products', 'catalog'] as const,
  nextSku: ['products', 'next-sku'] as const,
  barcodeCheck: (code: string, excludeId?: string) =>
    ['products', 'barcode-check', code, excludeId ?? null] as const,
  locations: ['locations'] as const,
  locationTree: ['locations', 'tree'] as const,
  location: (id: string) => ['locations', 'detail', id] as const,
  imports: ['data-tools', 'imports'] as const,
  backups: ['data-tools', 'backups'] as const,
  operations: ['operations'] as const,
  operationList: (filters: Record<string, unknown>) => ['operations', 'list', filters] as const,
  operation: (id: string) => ['operations', 'detail', id] as const,
  operationsSummary: ['operations', 'summary'] as const,
  transfers: ['transfers'] as const,
  transferList: (filters: Record<string, unknown>) => ['transfers', 'list', filters] as const,
  transfer: (id: string) => ['transfers', 'detail', id] as const,
  transfersSummary: ['transfers', 'summary'] as const,
  counts: ['counts'] as const,
  countList: (filters: Record<string, unknown>) => ['counts', 'list', filters] as const,
  count: (id: string) => ['counts', 'detail', id] as const,
  countLines: (id: string, query: Record<string, unknown>) =>
    ['counts', 'lines', id, query] as const,
  countsSummary: ['counts', 'summary'] as const,
  stock: ['stock'] as const,
  stockLevels: (query: Record<string, unknown>) => ['stock', 'levels', query] as const,
  stockLimits: (query: Record<string, unknown>) => ['stock', 'limits', query] as const,
  valuation: (siteId: string | null) => ['stock', 'valuation', siteId] as const,
  movements: (filters: Record<string, unknown>) => ['stock', 'movements', filters] as const,
  stockAt: (productId: string, date: string) => ['stock', 'at', productId, date] as const,
  alerts: ['alerts'] as const,
  alertList: (filters: Record<string, unknown>) => ['alerts', 'list', filters] as const,
  alertsSummary: ['alerts', 'summary'] as const,
  analytics: ['analytics'] as const,
  dashboard: (siteId: string | null) => ['analytics', 'dashboard', siteId] as const,
  report: (report: string, query: Record<string, unknown>) =>
    ['analytics', 'report', report, query] as const,
  attachments: (ownerType: string, ownerId: string) => ['attachments', ownerType, ownerId] as const,
  importJob: (id: string) => ['data-tools', 'imports', id] as const,
  search: (query: string) => ['search', query] as const,
  orders: ['orders'] as const,
  orderList: (query: Record<string, unknown>) => ['orders', 'list', query] as const,
  order: (id: string) => ['orders', 'detail', id] as const,
  deliveryNote: (id: string) => ['orders', 'detail', id, 'delivery-note'] as const,
  orderTimeline: (id: string) => ['orders', 'detail', id, 'timeline'] as const,
  customers: ['customers'] as const,
  customerList: (query: Record<string, unknown>) => ['customers', 'list', query] as const,
  customer: (id: string) => ['customers', 'detail', id] as const,
  customerTimeline: (id: string) => ['customers', 'detail', id, 'timeline'] as const,
  customerBalance: (id: string) => ['customers', 'detail', id, 'balance'] as const,
  customerPayments: (id: string, query: Record<string, unknown>) =>
    ['customers', 'detail', id, 'payments', query] as const,
  customerCheck: (phone: string, email: string, excludeId?: string) =>
    ['customers', 'check', phone, email, excludeId ?? null] as const,
  payments: ['payments'] as const,
  paymentList: (filters: Record<string, unknown>) => ['payments', 'list', filters] as const,
  codPending: ['payments', 'cod'] as const,
  document: (id: string) => ['documents', id] as const,
  documentLists: ['documents', 'list'] as const,
  documentList: (query: Record<string, unknown>) => ['documents', 'list', query] as const,
  documentFilters: ['documents', 'filters'] as const,
  promotions: ['promotions'] as const,
  promotionList: (query: Record<string, unknown>) => ['promotions', 'list', query] as const,
  productPromotions: (id: string) => ['promotions', 'product', id] as const,
  registers: ['registers'] as const,
  thisDevice: ['registers', 'this-device'] as const,
  cashSettings: ['shifts', 'cash-settings'] as const,
  shifts: ['shifts'] as const,
  shiftList: (filters: Record<string, unknown>) => ['shifts', 'list', filters] as const,
  shift: (id: string) => ['shifts', 'detail', id] as const,
  zReport: (id: string) => ['shifts', 'detail', id, 'z'] as const,
  // Suppliers and what is paid to them (docs/SUPPLIERS_PLAN.md).
  suppliers: ['suppliers'] as const,
  supplierList: (query: Record<string, unknown>) => ['suppliers', 'list', query] as const,
  supplierOptions: (q: string) => ['suppliers', 'options', q] as const,
  supplier: (id: string) => ['suppliers', 'detail', id] as const,
  supplierReceipts: (id: string) => ['suppliers', 'detail', id, 'receipts'] as const,
  supplierStatement: (id: string) => ['suppliers', 'detail', id, 'statement'] as const,
  supplierPayments: ['supplier-payments'] as const,
  supplierPaymentList: (query: Record<string, unknown>) =>
    ['supplier-payments', 'list', query] as const,
  supplierPayment: (id: string) => ['supplier-payments', 'detail', id] as const,
  receiptPayments: (operationId: string) => ['supplier-payments', 'receipt', operationId] as const,
  openReceipts: (supplierId: string | null, operationId: string | null) =>
    ['supplier-payments', 'open', supplierId, operationId] as const,
};
